import React, { useState, useMemo } from 'react';
import { 
  Calculator, 
  TrendingUp, 
  Clock, 
  DollarSign, 
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Zap
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Slider } from './ui/slider';
import { Button } from './ui/button';

const B2BCalculator = () => {
  const [monthlyVolume, setMonthlyVolume] = useState(30);
  const [hourlyRate, setHourlyRate] = useState(75);
  const [rejectionRate, setRejectionRate] = useState(15);

  const calculations = useMemo(() => {
    // Time calculations
    const manualHoursPerCase = 2.5; // Average hours for manual pre-check
    const petflycheckMinutesPerCase = 15; // Minutes with PetFlyCheck
    
    const manualHoursMonthly = monthlyVolume * manualHoursPerCase;
    const petflycheckHoursMonthly = (monthlyVolume * petflycheckMinutesPerCase) / 60;
    const hoursSaved = manualHoursMonthly - petflycheckHoursMonthly;
    
    // Cost calculations
    const manualLaborCost = manualHoursMonthly * hourlyRate;
    const rejectionCostPerIncident = 650; // Average cost per rejection
    const currentRejections = Math.round(monthlyVolume * (rejectionRate / 100));
    const reducedRejections = Math.round(currentRejections * 0.2); // 80% reduction
    const rejectionsAvoided = currentRejections - reducedRejections;
    const rejectionSavings = rejectionsAvoided * rejectionCostPerIncident;
    
    // PetFlyCheck cost estimate (Agency plan as baseline)
    const petflycheckCost = monthlyVolume <= 10 ? 149 : 
                           monthlyVolume <= 30 ? 399 : 
                           monthlyVolume <= 100 ? 799 : 1990;
    const extraChecks = Math.max(0, monthlyVolume - (monthlyVolume <= 10 ? 10 : monthlyVolume <= 30 ? 30 : monthlyVolume <= 100 ? 100 : 200));
    const extraCheckRate = monthlyVolume <= 10 ? 15 : monthlyVolume <= 30 ? 12 : monthlyVolume <= 100 ? 8 : 7;
    const totalPetflycheckCost = petflycheckCost + (extraChecks * extraCheckRate);
    
    // Labor savings (reduced time)
    const laborSavings = hoursSaved * hourlyRate;
    
    // Total savings
    const totalMonthlySavings = laborSavings + rejectionSavings - totalPetflycheckCost;
    const annualSavings = totalMonthlySavings * 12;
    const roi = ((totalMonthlySavings / totalPetflycheckCost) * 100).toFixed(0);
    
    return {
      hoursSaved: Math.round(hoursSaved),
      laborSavings: Math.round(laborSavings),
      rejectionsAvoided,
      rejectionSavings: Math.round(rejectionSavings),
      petflycheckCost: totalPetflycheckCost,
      totalMonthlySavings: Math.round(totalMonthlySavings),
      annualSavings: Math.round(annualSavings),
      roi: parseInt(roi),
      recommendedPlan: monthlyVolume <= 10 ? 'Solo' : monthlyVolume <= 30 ? 'Pro Desk' : monthlyVolume <= 100 ? 'Agency' : 'White Label'
    };
  }, [monthlyVolume, hourlyRate, rejectionRate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-6">
            <img src="/logo.png" alt="PetFlyCheck" className="w-8 h-8 object-contain" />
            <span className="text-white font-semibold">ROI Calculator</span>
            <Calculator className="w-5 h-5 text-blue-400" />
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Calculate Your <span className="text-blue-400">Savings</span>
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            See how much time and money you could save with PetFlyCheck.
            Adjust the sliders to match your operation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Input Controls */}
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-white mb-8 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Your Current Operation
              </h3>
              
              {/* Monthly Volume Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-slate-300 font-medium">Monthly Pet Travel Cases</label>
                  <span className="text-2xl font-bold text-blue-400">{monthlyVolume}</span>
                </div>
                <Slider
                  value={[monthlyVolume]}
                  onValueChange={(value) => setMonthlyVolume(value[0])}
                  min={5}
                  max={200}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>5</span>
                  <span>200+</span>
                </div>
              </div>

              {/* Hourly Rate Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-slate-300 font-medium">Team Hourly Rate (USD)</label>
                  <span className="text-2xl font-bold text-blue-400">${hourlyRate}</span>
                </div>
                <Slider
                  value={[hourlyRate]}
                  onValueChange={(value) => setHourlyRate(value[0])}
                  min={25}
                  max={150}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>$25</span>
                  <span>$150</span>
                </div>
              </div>

              {/* Rejection Rate Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-slate-300 font-medium">Current Rejection Rate</label>
                  <span className="text-2xl font-bold text-red-400">{rejectionRate}%</span>
                </div>
                <Slider
                  value={[rejectionRate]}
                  onValueChange={(value) => setRejectionRate(value[0])}
                  min={5}
                  max={30}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>5%</span>
                  <span>30%</span>
                </div>
              </div>

              {/* Assumptions Note */}
              <div className="bg-slate-800/50 rounded-lg p-4 text-sm text-slate-400">
                <p className="font-medium text-slate-300 mb-2">Calculation Assumptions:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Manual pre-check: ~2.5 hours per case</li>
                  <li>• With PetFlyCheck: ~15 minutes per case</li>
                  <li>• Average rejection cost: $650/incident</li>
                  <li>• PetFlyCheck reduces rejections by 80%</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Results Display */}
          <div className="space-y-6">
            {/* Main Savings Card */}
            <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 shadow-2xl shadow-blue-500/20">
              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <img src="/logo.png" alt="PetFlyCheck" className="w-10 h-10 object-contain" />
                    <div>
                      <p className="text-blue-200 text-sm font-medium">Recommended Plan</p>
                      <p className="text-white font-bold text-lg">{calculations.recommendedPlan}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-200 text-sm">Monthly Investment</p>
                    <p className="text-white font-bold text-xl">{formatCurrency(calculations.petflycheckCost)}</p>
                  </div>
                </div>
                
                <div className="border-t border-blue-500/30 pt-6">
                  <p className="text-blue-200 text-sm mb-2">Your Monthly Net Savings</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-white">
                      {formatCurrency(calculations.totalMonthlySavings)}
                    </span>
                    <span className="text-blue-200">/month</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-semibold">{calculations.roi}% ROI</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Breakdown */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-green-400" />
                    </div>
                    <span className="text-slate-400 text-sm">Time Saved</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{calculations.hoursSaved}h</p>
                  <p className="text-green-400 text-sm font-medium">{formatCurrency(calculations.laborSavings)} value</p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                    <span className="text-slate-400 text-sm">Rejections Avoided</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{calculations.rejectionsAvoided}</p>
                  <p className="text-green-400 text-sm font-medium">{formatCurrency(calculations.rejectionSavings)} saved</p>
                </CardContent>
              </Card>
            </div>

            {/* Annual Projection */}
            <Card className="bg-gradient-to-r from-emerald-600/20 to-emerald-500/10 border border-emerald-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-emerald-300 text-sm font-medium">Annual Projection</p>
                      <p className="text-3xl font-bold text-white">{formatCurrency(calculations.annualSavings)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-300 text-sm">per year</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Button */}
            <a href="#contact" className="block">
              <Button className="w-full bg-white hover:bg-slate-100 text-slate-900 font-bold py-6 text-lg shadow-xl hover:shadow-2xl transition-all">
                Start Saving with {calculations.recommendedPlan} Plan
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default B2BCalculator;
